import React, { useContext, useRef, useState, useEffect, useCallback } from "react";
import "./ScoreCard.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEarthAmericas } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useLocation } from "react-router-dom";
import { FaArrowLeftLong } from "react-icons/fa6";
import jsPDF from "jspdf";
import { IoMdDownload } from "react-icons/io";
import { ThemeContext } from "./context/themeContext";
import { AvatarContext } from "./context/AvatarContext";
import domtoimage from "dom-to-image-more";
import { useAuth } from "./auth/useAuth";
import { io } from "socket.io-client";
import { motion, useReducedMotion } from "framer-motion";
import AIMentorPanel from "./components/upgraded/AIMentorPanel";
import "./components/upgraded/AIMentorPanel.css";
import GTMFlowchartPanel from "./components/upgraded/GTMFlowchartPanel";
import "./components/upgraded/GTMFlowchartPanel.css";
import UsageBadge from "./components/upgraded/UsageBadge";
import "./components/upgraded/UsageBadge.css";
import { apiFetch } from "./utils/apiFetch";

export default function ScoreCard({ setToggle, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const prefersReducedMotion = useReducedMotion();
  const reportRef = useRef(null);
  const { avatar } = useContext(AvatarContext);
  const { activeTheme } = useContext(ThemeContext);
  const { user } = useAuth();

  const fromReportsHub = location.state?.fromReportsHub === true;
  // Full Analysis mode: show breakdown + GTM + AI Mentor; otherwise scorecard-only
  const isFullAnalysis  = location.state?.openAnalysis === true;

  // Scorecard data from backend
  const res = JSON.parse(localStorage.getItem("scorecardData") || "{}");

  // Live score state (starts at L1 score, animates to AI score)
  // For fresh AI-processing submissions start at the raw lookup score so the animation is visible
  const _prelimScore = (!fromReportsHub && (res?.aiProcessing ?? false))
    ? (res?.scoreBreakdown?.lookupScore ?? res?.totalScore ?? 0)
    : (res?.totalScore ?? 0);
  const [founderName, setFounderName]           = useState(res?.fullName ?? "Founder");
  const [displayScore, setDisplayScore]         = useState(_prelimScore);
  const [finalScore, setFinalScore]             = useState(res?.totalScore ?? 0);
  // When coming from ReportsHub AI is already done - never start in processing state
  const [aiProcessing, setAiProcessing]         = useState(fromReportsHub ? false : (res?.aiProcessing ?? false));
  const [processingDone, setProcessingDone]     = useState(fromReportsHub ? true  : !res?.aiProcessing);
  const [scoreBreakdown, setScoreBreakdown]     = useState(res?.scoreBreakdown ?? null);
  const [crossCheckResults, setCrossCheckResults] = useState(res?.crossCheckResults ?? null);
  // eslint-disable-next-line no-unused-vars
  const [aiReport, setAiReport]                 = useState(res?.aiReport ?? null);
  const [sectionScoresState, setSectionScoresState] = useState(res?.sectionScores ?? null);
  const [reportIdState, setReportIdState]       = useState(res?.id ?? res?.recordId ?? null);
  const [showBreakdown, setShowBreakdown]       = useState(isFullAnalysis);
  const [processingLabel, setProcessingLabel]   = useState("Analyzing your idea with AI...");

  // Persisted analysis panels (loaded from DB via report object)
  const [gtmMermaid, setGtmMermaid]       = useState((res && res.sectionScores && res.sectionScores.gtmFlowchart) || "");
  const [mentorHistory, setMentorHistory] = useState((res && res.sectionScores && res.sectionScores.mentorHistory) || []);

  // ── Payment gate ─────────────────────────────────────────────
  const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const isScoreLocked = !isLocal && (res?.requiresPayment === true || res?.paymentStatus === "PENDING") && localStorage.getItem("scorecardAccess") !== "true";
  const [showPaymentModal, setShowPaymentModal] = useState(isScoreLocked);
  const [payLoading, setPayLoading] = useState(false);
  const [showPaySuccess, setShowPaySuccess] = useState(false);

  // ── Animated counter ─────────────────────────────────────────
  const animateScore = useCallback((from, to) => {
    const duration = 1200;
    const steps = 60;
    const stepDuration = duration / steps;
    const diff = to - from;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setDisplayScore(Math.round(from + diff * eased));
      if (step >= steps) {
        clearInterval(timer);
        setDisplayScore(to);
      }
    }, stepDuration);
  }, []);

  // ── Fetch report by ID from backend if needed or on mount ──
  useEffect(() => {
    if (!user?.user_id) return;

    const targetReportId = reportIdState || localStorage.getItem("payingReportId") || localStorage.getItem("ideaValidationPaymentRecordId");

    const loadReportDetails = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
        const url = `${backendUrl}/idea-validation/report-list/${user.user_id}`;
        const response = await apiFetch(`/idea-validation/report-list/${user.user_id}`);
        if (!response.ok) throw new Error(`Failed to fetch reports: ${response.status}`);

        const data = await response.json();
        if (data.success && data.reports && data.reports.length > 0) {
          const report = targetReportId
            ? data.reports.find((r) => String(r.id) === String(targetReportId)) || data.reports[0]
            : data.reports[0];

          if (report) {
            // Heal local storage keys
            localStorage.setItem("payingReportId", report.id);
            localStorage.setItem("ideaValidationPaymentRecordId", report.id);

            // Consider AI done as soon as backend wrote any aiResults object
            const hasAiResults = report.sectionScores?.aiResults != null;
            const updatedScore = (hasAiResults && report.sectionScores.aiResults.finalScore)
              ? report.sectionScores.aiResults.finalScore
              : report.totalScore;
            
            const sc = report.sectionScores || {};
            const _consistencyAdj = sc.consistencyCheck?.adjustment || 0;
            const _coherenceAdj   = sc.aiResults?.coherenceAdjustment || 0;
            const _aiTextScore    = sc.aiResults?.aiTextScore || 0;
            // lookupScore = pure section scores only (no consistency, no AI adjustments)
            const computedBreakdown = hasAiResults ? {
              lookupScore: report.totalScore - _aiTextScore - _consistencyAdj - _coherenceAdj,
              consistencyAdjustment: _consistencyAdj,
              aiTextScore: sc.aiResults?.aiTextScore ?? null,
              coherenceAdjustment: sc.aiResults?.coherenceAdjustment ?? null,
            } : {
              lookupScore: report.totalScore - _consistencyAdj,
              consistencyAdjustment: _consistencyAdj,
              aiTextScore: null,
              coherenceAdjustment: null,
            };

            const computedCrossCheck = {
              consistencyFlags: sc.consistencyCheck?.flags || [],
              coherenceFlags: sc.aiResults?.coherenceFlags || [],
              coherenceAssessment: sc.aiResults?.coherenceAssessment || "",
              aiFieldScores: sc.aiResults?.fieldScores || null,
              aiInsights: sc.aiResults?.insights || [],
            };

            const updatedData = {
              ...report,
              totalScore: updatedScore,
              aiProcessing: !hasAiResults,
              scoreBreakdown: computedBreakdown,
              crossCheckResults: computedCrossCheck,
              aiReport: sc.aiReport || null,
            };
            localStorage.setItem("scorecardData", JSON.stringify(updatedData));

            setFounderName(report.fullName ?? "Founder");
            setReportIdState(report.id);
            setSectionScoresState(report.sectionScores);
            setFinalScore(updatedScore);
            setScoreBreakdown(computedBreakdown);
            setCrossCheckResults(computedCrossCheck);
            setAiReport(sc.aiReport || null);
            // Hydrate persisted analysis panels
            if (sc.gtmFlowchart) setGtmMermaid(sc.gtmFlowchart);
            if (sc.mentorHistory?.length) setMentorHistory(sc.mentorHistory);

            if (hasAiResults) {
              setAiProcessing(false);
              setProcessingDone(true);
              if (fromReportsHub) {
                // Viewing saved report: show final AI score immediately
                setDisplayScore(updatedScore);
              } else {
                // Fresh submission: animate from the raw lookup score up to the AI-enhanced final score
                const startScore = computedBreakdown.lookupScore || 0;
                setDisplayScore(startScore);
                setTimeout(() => animateScore(startScore, updatedScore), 400);
              }
            } else if (!fromReportsHub && res?.aiProcessing !== false) {
              // Still processing (only for fresh submissions, not ReportsHub views)
              setAiProcessing(true);
              setProcessingDone(false);
              setDisplayScore(computedBreakdown.lookupScore || report.totalScore);
            }
          }
        }
      } catch (err) {
        console.error("❌ ScoreCard: Failed to load report details:", err);
      }
    };

    loadReportDetails();
  }, [user?.user_id, reportIdState]);

  // ── Socket.io + DB polling fallback for AI completion ────────
  useEffect(() => {
    if (!aiProcessing || !user?.user_id) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
    let done = false;
    let pollTimer = null;
    let pollStart = null;

    // Single apply function — whichever arrives first (socket or poll) wins
    const applyAiResult = (payload, fromSocket = false) => {
      if (done) return;
      done = true;
      clearTimeout(pollStart);
      clearInterval(pollTimer);

      const newScore = payload.finalScore ?? payload.totalScore;
      if (newScore == null) return;

      setFinalScore(newScore);
      setAiProcessing(false);
      setProcessingDone(true);
      if (payload.scoreBreakdown)    setScoreBreakdown(payload.scoreBreakdown);
      if (payload.crossCheckResults) setCrossCheckResults(payload.crossCheckResults);
      if (payload.aiReport)          setAiReport(payload.aiReport);
      const newId = payload.recordId || payload.id;
      if (newId) setReportIdState(newId);

      const updated = {
        ...res,
        id: newId || res.id || res.recordId,
        totalScore: newScore,
        aiProcessing: false,
        scoreBreakdown: payload.scoreBreakdown,
        crossCheckResults: payload.crossCheckResults,
        aiReport: payload.aiReport,
      };
      localStorage.setItem("scorecardData", JSON.stringify(updated));
      animateScore(displayScore, newScore);
      if (fromSocket) socket.disconnect();
    };

    // ── Socket (primary) ─────────────────────────────────────────
    const socket = io(backendUrl, {
      withCredentials: true,
      transports: ["websocket", "polling"],
    });

    socket.on("connect", () => {
      socket.emit("join_user_room", user.user_id);
    });

    socket.on("score:ai_complete", (payload) => {
      clearInterval(labelTimer);
      applyAiResult(payload, true);
    });

    // ── Polling fallback (starts 12s after mount) ─────────────────
    const pollOnce = async () => {
      if (done) return;
      try {
        const r = await apiFetch(`/idea-validation/report-list/${user.user_id}`);
        if (!r.ok) return;
        const data = await r.json();
        if (!data.success || !data.reports?.length) return;

        const targetId = localStorage.getItem("payingReportId") || localStorage.getItem("ideaValidationPaymentRecordId");
        const report = targetId
          ? (data.reports.find((rp) => String(rp.id) === String(targetId)) || data.reports[0])
          : data.reports[0];

        if (!report?.sectionScores?.aiResults) return;

        const ai = report.sectionScores.aiResults;
        clearInterval(labelTimer);
        applyAiResult({
          finalScore: ai.finalScore ?? report.totalScore,
          id: report.id,
          scoreBreakdown: {
            lookupScore: report.totalScore - (ai.aiTextScore || 0),
            consistencyAdjustment: report.sectionScores.consistencyCheck?.adjustment || 0,
            aiTextScore: ai.aiTextScore,
            coherenceAdjustment: ai.coherenceAdjustment,
          },
          crossCheckResults: {
            consistencyFlags: report.sectionScores.consistencyCheck?.flags || [],
            coherenceFlags: ai.coherenceFlags || [],
            coherenceAssessment: ai.coherenceAssessment || "",
            aiFieldScores: ai.fieldScores || null,
            aiInsights: ai.insights || [],
          },
          aiReport: report.sectionScores.aiReport || null,
        });
      } catch (err) {
        console.warn("[ScoreCard] poll failed:", err.message);
      }
    };

    pollStart = setTimeout(() => {
      pollOnce(); // immediate check at 3s
      pollTimer = setInterval(pollOnce, 5000); // then every 5s
    }, 3000);

    // Cycle processing labels for UX
    const labels = [
      "Analyzing your idea with AI...",
      "Scoring problem clarity & uniqueness...",
      "Checking coherence across all answers...",
      "Generating personalized report...",
      "Finalizing your score...",
    ];
    let labelIdx = 0;
    const labelTimer = setInterval(() => {
      labelIdx = (labelIdx + 1) % labels.length;
      setProcessingLabel(labels[labelIdx]);
    }, 3000);

    return () => {
      clearTimeout(pollStart);
      clearInterval(pollTimer);
      clearInterval(labelTimer);
      socket.disconnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiProcessing, user?.user_id]);

  // ── Score helpers ─────────────────────────────────────────────
  const score = displayScore;

  const getClassification = (s) => {
    if (s >= 500) return "Validated Visionary";
    if (s >= 400) return "Promising Potential";
    if (s >= 300) return "Needs Work";
    return "High Risk / Invalid Idea";
  };

  const getScoreColor = (s) => {
    if (s >= 500) return "#18a558";
    if (s >= 400) return "#7fde9a";
    if (s >= 300) return "#ffc66a";
    return "#ff6b6b";
  };

  const getShortDescription = (s) => {
    if (s >= 500) return "Exceptional innovation, validated demand, investor-ready pitch clarity.";
    if (s >= 400) return "Strong problem–solution fit, needs refinement in pricing or GTM.";
    if (s >= 300) return "Partial clarity; strengthen model, metrics, and feasibility proof.";
    return "High uncertainty; validation gaps in market need or product feasibility.";
  };

  // ── SVG ring ─────────────────────────────────────────────────
  const clamped      = Math.min(Math.max(score, 0), 600);
  const fillRatio    = clamped / 600;
  const radius       = 50;
  const circumference = 2 * Math.PI * radius;
  const dash         = circumference * fillRatio;
  const ringColor    = getScoreColor(clamped);

  // ── Navigation ───────────────────────────────────────────────
  const handleBackClick = () => {
    setToggle(false);
    onClose();
    if (location.state?.fromReportsHub) {
      navigate(`/reports-hub`);
    } else {
      navigate("/dashboard");
    }
  };

  const handleViewCompleteReport = () => {
    setToggle(false);
    onClose();
    navigate(`/reports-hub`);
  };

  const handleWhyReport = () => {
    setToggle(false);
    onClose();
    navigate(`/validation-benefits`);
  };

  // ── PDF Download ─────────────────────────────────────────────
  const downloadReport = async () => {
    const element = reportRef.current;
    if (!element) return;
    try {
      const dataUrl = await domtoimage.toPng(element, {
        height: element.offsetHeight,
        width: element.offsetWidth,
        style: { transform: "scale(1)", margin: 0 },
        bgcolor: "#ffffff",
      });
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (element.offsetHeight * pdfWidth) / element.offsetWidth;
      pdf.addImage(dataUrl, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save("ScoreCard_By_MARSMATEAI.pdf");
    } catch (error) {
      console.error("PDF error:", error);
      alert("download feature is coming soon");
    } finally {
      setToggle(false);
      onClose();
    }
  };

  // ── Scorecard unlock payment ─────────────────────────────────
  const handleScorecardPayment = async () => {
    const BACKEND = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
    const recordId = reportIdState
      || localStorage.getItem("payingReportId")
      || localStorage.getItem("ideaValidationPaymentRecordId");

    if (!recordId) { alert("Unable to proceed: missing report reference."); return; }

    setPayLoading(true);
    try {
      // Load Razorpay SDK
      await new Promise((resolve, reject) => {
        if (window.Razorpay) { resolve(); return; }
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });

      const res2 = await fetch(`${BACKEND}/idea-validation/create-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const orderData = await res2.json();
      if (!orderData?.success) { alert("Failed to create payment order."); return; }

      const razorpayKey = orderData.key_id
        || process.env.REACT_APP_RAZORPAY_KEY_ID_IDEA_VALIDATION
        || process.env.REACT_APP_RAZORPAY_KEY_ID;
      if (!razorpayKey) { alert("Payment configuration error. Please contact support."); return; }

      const options = {
        key: razorpayKey,
        amount: orderData.amount,
        currency: "INR",
        name: "Marsmate AI",
        description: "Idea Validation Scorecard Unlock",
        order_id: orderData.order_id,
        prefill: { name: user?.name || "User", email: user?.email || "" },
        theme: { color: "#F45900" },
        handler: async (response) => {
          try {
            const verifyRes = await fetch(`${BACKEND}/idea-validation/verify-payment`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                recordId,
              }),
              credentials: "include",
            });
            const verifyData = await verifyRes.json();
            if (verifyData.success) {
              localStorage.setItem("scorecardAccess", "true");
              localStorage.setItem("scorecardtoken", "true");
              localStorage.setItem("payingReportId", recordId);
              setShowPaymentModal(false);
              setShowPaySuccess(true);
              // Mark scorecard as AI-processing so socket listener re-activates
              setAiProcessing(true);
              setProcessingDone(false);
            } else {
              alert("Payment verification failed.");
            }
          } catch (err) {
            console.error(err);
            alert("Error verifying payment.");
          }
        },
        modal: { ondismiss: () => {} },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (r) => alert("Payment Failed: " + r.error.description));
      rzp.open();
    } catch (err) {
      console.error(err);
      alert("Error initiating payment.");
    } finally {
      setPayLoading(false);
    }
  };

  // ── Render flag badge ─────────────────────────────────────────
  const renderFlags = (flags = [], type = "consistency") => {
    if (!flags || flags.length === 0) return null;
    return (
      <div className="sc-flags-list">
        {flags.map((f, i) => {
          const isBonus   = (f.adjustment > 0) || f.type === "coherenceBonus";
          const isNeutral = f.adjustment === 0;
          return (
            <div
              key={`${type}-${i}`}
              className={`sc-flag-item ${isBonus ? "sc-flag-bonus" : isNeutral ? "sc-flag-neutral" : "sc-flag-penalty"}`}
            >
              <span className="sc-flag-badge">
                {isBonus ? "+" : ""}{f.adjustment ?? 0} pts
              </span>
              <span className="sc-flag-text">
                <strong>{f.ruleId || f.type || "Rule"}</strong>: {f.description}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Render AI field score bars ────────────────────────────────
  const renderAIFieldScores = (fieldScores) => {
    if (!fieldScores) return null;
    const maxMap = { problem: 20, ideaDescription: 25, revenueStreams: 15, costStructure: 15, northStarMetric: 10, ideaTitle: 5 };
    const labelMap = { problem: "Problem", ideaDescription: "Solution", revenueStreams: "Revenue", costStructure: "Cost Structure", northStarMetric: "North Star", ideaTitle: "Title" };
    return (
      <div className="sc-field-scores">
        {Object.entries(fieldScores).map(([key, rawPts]) => {
          const pts = typeof rawPts === "number" ? rawPts : Number(rawPts) || 0;
          const max = maxMap[key] || 10;
          const pct = Math.round((pts / max) * 100);
          return (
            <div className="sc-field-row" key={key}>
              <span className="sc-field-label">{labelMap[key] || key}</span>
              <div className="sc-field-bar-wrap">
                <div className="sc-field-bar" style={{ width: `${pct}%`, backgroundColor: pct >= 70 ? "#18a558" : pct >= 40 ? "#ffc66a" : "#ff6b6b" }} />
              </div>
              <span className="sc-field-pts">{pts}/{max}</span>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Render category score bars ────────────────────────────────
  const renderCategoryScores = (sectionScores) => {
    if (!sectionScores) return null;
    const maxMap = {
      customerEmpathy: 100,
      solutionViability: 90,
      marketOpportunity: 80,
      founderReadiness: 100,
      businessModel: 80,
      goToMarket: 90,
      riskCompliance: 60
    };
    const labelMap = {
      customerEmpathy: "Customer Empathy",
      solutionViability: "Solution Viability",
      marketOpportunity: "Market Opportunity",
      founderReadiness: "Founder Readiness",
      businessModel: "Business Model",
      goToMarket: "Go-To-Market",
      riskCompliance: "Risk & Compliance"
    };

    return (
      <div className="sc-field-scores">
        {Object.entries(maxMap).map(([key, max]) => {
          const pts = sectionScores[key] ?? 0;
          const pct = Math.round((pts / max) * 100);
          return (
            <div className="sc-field-row" key={key}>
              <span className="sc-field-label">{labelMap[key]}</span>
              <div className="sc-field-bar-wrap">
                <div 
                  className="sc-field-bar" 
                  style={{ 
                    width: `${pct}%`, 
                    backgroundColor: pct >= 80 ? "#18a558" : pct >= 50 ? "#ffc66a" : "#ff6b6b" 
                  }} 
                />
              </div>
              <span className="sc-field-pts">{pts}/{max}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={`scorecard-wrapper${showPaymentModal ? " sc-locked" : ""}`}>
      {/* ── Payment success overlay ──────────────────────────────── */}
      {showPaySuccess && (
        <div className="sc-lock-overlay" onClick={() => setShowPaySuccess(false)}>
          <div className="sc-lock-modal sc-pay-success-modal" onClick={e => e.stopPropagation()}>
            <button className="sc-modal-close-btn" onClick={() => setShowPaySuccess(false)} title="Close">✕</button>
            <div className="sc-pay-success-tick">✓</div>
            <h3 className="sc-lock-title">Payment Successful!</h3>
            <p className="sc-lock-desc">Your scorecard has been unlocked. AI analysis is now running.</p>
            <button className="sc-lock-btn" onClick={() => setShowPaySuccess(false)}>
              Continue
            </button>
          </div>
        </div>
      )}
      {/* ── Payment gate overlay ─────────────────────────────────── */}
      {showPaymentModal && (
        <div className="sc-lock-overlay">
          <div className="sc-lock-modal">
            <button
              className="sc-modal-close-btn"
              onClick={() => { setShowPaymentModal(false); navigate("/dashboard"); }}
              title="Close"
            >✕</button>
            <div className="sc-lock-icon">🔒</div>
            <h3 className="sc-lock-title">Unlock Your Scorecard</h3>
            <p className="sc-lock-desc">
              Your free Idea Validation has been used. Unlock this scorecard for just ₹10.
            </p>
            <div className="sc-lock-price">₹10</div>
            <button
              className="sc-lock-btn"
              onClick={handleScorecardPayment}
              disabled={payLoading}
            >
              {payLoading ? "Processing..." : "Pay ₹10 to Unlock"}
            </button>
          </div>
        </div>
      )}
      {/* Monthly usage indicator */}
      {user?.user_id && (
        <div style={{ width: "55%", marginLeft: "6%", marginBottom: 8 }}>
          <UsageBadge userId={user.user_id} />
        </div>
      )}
      {/* ── TOP BUTTONS ─────────────────────────────────── */}
      <div className="download-container2">
        <button className="back-btn2" onClick={handleBackClick}>
          <FaArrowLeftLong size={26} />
        </button>
        <button className="download-btn2" onClick={downloadReport}>
          Download <IoMdDownload size={26} />
        </button>
      </div>

      {/* ── MAIN CARD ────────────────────────────────────── */}
      <motion.div
        className={`Scorecard ${activeTheme === "dark" ? "dark-mode" : ""}`}
        ref={reportRef}
        initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.96, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      >

        {/* HEADER */}
        <div className="scorecard-header">
          <div className="foresight-top">
            <button className="foresight-btn">
              <FontAwesomeIcon icon={faEarthAmericas} /> Foresight
            </button>
          </div>
          <div className="profile-center">
            <div className="scorecard-profile-wrapper">
              <div className="scorecard-profile-outer">
                <div className="scorecard-profile-inner">
                  {avatar ? (
                    <img src={avatar} alt="Profile" />
                  ) : (
                    <span>{founderName?.[0]?.toUpperCase() || "?"}</span>
                  )}
                </div>
                <div className="scorecard-profile-badge">+{score}</div>
              </div>
            </div>
          </div>
          <span className="span-text1">
            {founderName ? `HEY ${founderName.toUpperCase()}` : "HEY FOUNDER"}
          </span>
        </div>

        {/* SCORE LABEL */}
        <span className="span-text2">{getClassification(score)}</span>

        {/* SCORE RING */}
        <div className="outer-ring-svg-wrapper">
          <span className="score-label-left">0</span>
          <svg viewBox="0 0 120 120" width="260" height="260">
            <circle cx="60" cy="60" r={radius} stroke="#e6e6e6" strokeWidth={3} fill="none" />
            <circle
              cx="60" cy="60" r={radius}
              stroke={ringColor}
              strokeWidth={3}
              strokeLinecap="round"
              fill="none"
              transform="rotate(-180 60 60)"
              strokeDasharray={`${dash} ${circumference - dash}`}
              style={{ transition: "stroke-dasharray 1.2s cubic-bezier(.22,.9,.3,1), stroke 0.5s ease" }}
            />
          </svg>
          <div className="score-display" style={{ color: ringColor }}>{score}</div>
          <span className="score-label-right">600</span>
        </div>

        {/* ── AI PROCESSING BANNER ──────────────────────── */}
        {aiProcessing && (
          <div className="sc-processing-banner">
            <div className="sc-processing-dots">
              <span /><span /><span />
            </div>
            <p className="sc-processing-label">{processingLabel}</p>
            <p className="sc-processing-sub">
              Preliminary score shown · AI analysis in progress
            </p>
          </div>
        )}

        {/* ── AI COMPLETE BADGE ─────────────────────────── */}
        {processingDone && scoreBreakdown && (
          <div className="sc-ai-complete-badge">
            ✦ AI-Enhanced Score · All {crossCheckResults?.consistencyFlags?.length > 0 ? crossCheckResults.consistencyFlags.length : "0"} consistency checks applied
          </div>
        )}

        {/* SUMMARY */}
        <div className="startupidea">
          <span className="span-text3">STARTUP VALIDATION SUMMARY:</span>
          <span className="span-text4">{getShortDescription(score)}</span>
        </div>

        {/* Score breakdown — only in Full Analysis mode */}
        {isFullAnalysis && processingDone && scoreBreakdown && (
          <div className="sc-breakdown-section">
            <button
              className="sc-breakdown-toggle"
              onClick={() => setShowBreakdown((p) => !p)}
            >
              {showBreakdown ? "▲ Hide Score Breakdown" : "▼ View Score Breakdown & Flags"}
            </button>

            {showBreakdown && (
              <div className="sc-breakdown-body">

                {/* Score layers */}
                <div className="sc-breakdown-grid">
                  <div className="sc-breakdown-row">
                    <span className="sc-breakdown-label">📊 Lookup Score</span>
                    <span className="sc-breakdown-val sc-val-neutral">{scoreBreakdown.lookupScore ?? "—"}</span>
                  </div>
                  <div className="sc-breakdown-row">
                    <span className="sc-breakdown-label">🔗 Consistency Adj.</span>
                    <span className={`sc-breakdown-val ${scoreBreakdown.consistencyAdjustment >= 0 ? "sc-val-bonus" : "sc-val-penalty"}`}>
                      {scoreBreakdown.consistencyAdjustment >= 0 ? "+" : ""}{scoreBreakdown.consistencyAdjustment ?? 0}
                    </span>
                  </div>
                  {scoreBreakdown.aiTextScore !== null && scoreBreakdown.aiTextScore !== undefined && (
                    <div className="sc-breakdown-row">
                      <span className="sc-breakdown-label">🤖 AI Text Score</span>
                      <span className="sc-breakdown-val sc-val-bonus">+{scoreBreakdown.aiTextScore}</span>
                    </div>
                  )}
                  {scoreBreakdown.coherenceAdjustment !== null && scoreBreakdown.coherenceAdjustment !== undefined && (
                    <div className="sc-breakdown-row">
                      <span className="sc-breakdown-label">🧠 Coherence Adj.</span>
                      <span className={`sc-breakdown-val ${scoreBreakdown.coherenceAdjustment >= 0 ? "sc-val-bonus" : "sc-val-penalty"}`}>
                        {scoreBreakdown.coherenceAdjustment >= 0 ? "+" : ""}{scoreBreakdown.coherenceAdjustment}
                      </span>
                    </div>
                  )}
                  <div className="sc-breakdown-row sc-breakdown-total">
                    <span className="sc-breakdown-label">🏆 Final Score</span>
                    <span className="sc-breakdown-val sc-val-neutral">{finalScore}/600</span>
                  </div>
                </div>

                {/* Dimensions Breakdown */}
                {sectionScoresState && (
                  <div className="sc-flags-section">
                    <h4 className="sc-flags-heading">📊 Startup Dimensions Breakdown</h4>
                    {renderCategoryScores(sectionScoresState)}
                  </div>
                )}

                {/* Consistency flags */}
                {crossCheckResults?.consistencyFlags?.length > 0 && (
                  <div className="sc-flags-section">
                    <h4 className="sc-flags-heading">🔗 Consistency Checks</h4>
                    {renderFlags(crossCheckResults.consistencyFlags, "consistency")}
                  </div>
                )}

                {/* Coherence flags */}
                {crossCheckResults?.coherenceFlags?.length > 0 && (
                  <div className="sc-flags-section">
                    <h4 className="sc-flags-heading">🧠 Coherence Analysis</h4>
                    {renderFlags(crossCheckResults.coherenceFlags, "coherence")}
                    {crossCheckResults.coherenceAssessment && (
                      <p className="sc-coherence-text">{crossCheckResults.coherenceAssessment}</p>
                    )}
                  </div>
                )}

                {/* AI field scores */}
                {crossCheckResults?.aiFieldScores && Object.keys(crossCheckResults.aiFieldScores).length > 0 && (
                  <div className="sc-flags-section">
                    <h4 className="sc-flags-heading">🤖 AI Free-Text Scores</h4>
                    {renderAIFieldScores(crossCheckResults.aiFieldScores)}
                  </div>
                )}

              </div>
            )}
          </div>
        )}

        {/* ACTION BUTTONS */}
        <div className="scorecardbuttons">
          <button className="ScoreButton1" onClick={handleViewCompleteReport}>
            View Complete Report
          </button>
          <button className="ScoreButton2" onClick={handleWhyReport}>
            Why Should I Get Idea Validation Report
          </button>
        </div>
      </motion.div>

      {/* GTM flowchart + AI Mentor — only in Full Analysis mode */}
      {isFullAnalysis && processingDone && reportIdState && (
        <>
          <GTMFlowchartPanel reportId={reportIdState} initialMermaid={gtmMermaid} score={score} />
          <AIMentorPanel reportId={reportIdState} userId={user?.user_id} initialHistory={mentorHistory} />
        </>
      )}
    </div>
  );
}
